import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  Clock,
  AlertCircle,
  ClipboardList,
  AlertTriangle,
  Search,
  UserPlus,
  UserCog,
  UserCheck,
  Users,
  X,
  ListTodo,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { KpiStatCard } from "@/components/ui/kpi-stat-card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { ScrollableTabsList } from "@/components/ui/scrollable-tabs-list";
import { useAuth } from "@/hooks/useAuth";
import { useInspectionPermissions } from "@/hooks/useInspectionPermissions";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { ResolveDefectDialog } from "@/components/defects/ResolveDefectDialog";
import { VerifyDefectDialog } from "@/components/defects/VerifyDefectDialog";
import { CancelDefectDialog } from "@/components/defects/CancelDefectDialog";
import { ReopenDefectDialog } from "@/components/defects/ReopenDefectDialog";
import { DeleteDefectDialog } from "@/components/defects/DeleteDefectDialog";
import { DefectBaseDialog } from "@/components/defects/DefectBaseDialog";
import { useDefectEditTarget, defectsKeys } from "@/hooks/useDefectsQuery";
import { EditInspectionDialog } from "@/components/inspection/EditInspectionDialog";
import { CancelInspectionDialog } from "@/components/inspection/CancelInspectionDialog";
import { DeleteInspectionDialog } from "@/components/inspection/DeleteInspectionDialog";
import { DefectCard, DefectCardData } from "@/components/defects/DefectCard";
import { InspectionCard, InspectionCardData } from "@/components/inspection/InspectionCard";
import { InspectionPointCard } from "@/components/inspection/InspectionPointCard";
import { PtActionCard } from "@/components/tasks/PtActionCard";
import { DeputyTaskCard } from "@/components/tasks/DeputyTaskCard";
import { useDeputyPrincipals } from "@/hooks/useDeputyPrincipals";
import { PageContainer } from "@/components/layout/PageContainer";
import { TasksSkeleton } from "@/components/tasks/TasksSkeleton";
import { OverdueBadge } from "@/components/ui/overdue-badge";
import {
  useAssignableProfilesForDefects,
  useAssignableProfilesForInspections,
} from "@/hooks/useAssignableProfiles";
import { BulkReassignDialog } from "@/components/common/BulkReassignDialog";
import { notifyDefectStatusChanged, notifyBulkReassignment } from "@/lib/notifications";
import { useTasksQuery, tasksKeys, type UnifiedTask } from "@/hooks/useTasksQuery";
import { useQueryClient } from "@tanstack/react-query";
import { TeamFilterDropdown } from "@/components/team/TeamFilterDropdown";
import { useActiveTeam } from "@/hooks/useActiveTeam";
import { resolveTeamScopedFilter } from "@/lib/teamScope";
import { isTerminalDefectStatus } from "@/config/defectTransitions";
import {
  filterTasks,
  computeTaskStats,
  getUnavailableAssigneeTasks,
  computeWeekProgress,
  computeProgressBarWidth,
} from "@/components/tasks/tasksHelpers";
import { Progress } from "@/components/ui/progress";

// Große Seiten-Komponente (Task-Dashboard-Dispatcher): die L6-4-Tabs ("Meine Gruppen" + "Prüfpunkte &
// Maßnahmen") erweitern den bestehenden Render-Switch um wenige Branches; Karten-Duplikation wurde
// bereits in renderUnassigned*Card extrahiert. Restkomplexität ist vorbestehend/strukturell.
// fallow-ignore-next-line complexity
export default function Tasks() {
  const { t } = useTranslation();
  const { user, isQualitaetsmanager, isReadOnlyDefectUser, isExternalDefectUser } = useAuth();
  const { canDeleteInspection } = useInspectionPermissions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [teamFilter, setTeamFilter] = useState<string | null>(null);
  const { activeTeamId, isLoading: isActiveTeamLoading } = useActiveTeam();
  const effectiveTeamFilter = resolveTeamScopedFilter(teamFilter, activeTeamId);

  // React Query hook for tasks
  const {
    tasks,
    isLoading: isTasksLoading,
    isFetching,
  } = useTasksQuery({
    teamId: effectiveTeamFilter,
    enabled: !isActiveTeamLoading,
  });
  const isLoading = isActiveTeamLoading || isTasksLoading;

  // Helper to invalidate tasks cache
  const invalidateTasks = () => {
    queryClient.invalidateQueries({ queryKey: tasksKeys.all });
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState(() => {
    const statusParam = searchParams.get("status");
    if (statusParam === "all") return "all";
    // Accept both 'open' and 'openAll' from URL, map to 'openAll' (actual tab value)
    if (statusParam === "open" || statusParam === "openAll") return "openAll";
    if (statusParam === "overdue") return "overdue";
    if (statusParam === "verification") return "verification";
    // TES-2053: Deeplink auf die "Zu verifizieren (Prüfungen)"-Sektion.
    if (statusParam === "inspectionVerification") return "inspectionVerification";
    // TES-2076: Deeplink auf die "Aufgaben als Stellvertreter"-Sektion.
    if (statusParam === "deputy") return "deputy";
    if (statusParam === "unavailableAssignee") return "unavailableAssignee";
    // Default to 'openAll' to hide completed by default
    return "openAll";
  });
  const [resolvingDefect, setResolvingDefect] = useState<{ id: string; title: string } | null>(
    null,
  );
  const [verifyingDefect, setVerifyingDefect] = useState<{
    id: string;
    title: string;
    assigned_to?: string | null;
    created_by?: string | null;
  } | null>(null);

  // Assignable users from cached hooks
  const { data: assignableUsers = [] } = useAssignableProfilesForDefects();
  const { data: inspectionAssignableUsers = [] } = useAssignableProfilesForInspections();

  // Defect dialogs state
  const [editingDefect, setEditingDefect] = useState<DefectCardData | null>(null);
  const [editDefectDialogOpen, setEditDefectDialogOpen] = useState(false);
  // TES-1962: DefectBaseDialog braucht das volle DefectWithDetails + Gruppe beim Mount.
  const { data: defectEditTarget, isError: defectEditTargetError } = useDefectEditTarget(
    editingDefect?.id ?? null,
  );

  // TES-2042 (Copilot #786): Paritaet zu Defects.tsx — schlaegt der Edit-Target-Fetch fehl,
  // waere der edit-first Karten-Klick ein stiller Toter Klick. Fallback auf die read-only
  // Detail-Ansicht (Deeplink) + geerrorten Cache-Eintrag entfernen, damit ein erneuter
  // Versuch nach transienten Fehlern wieder editieren kann.
  useEffect(() => {
    if (!defectEditTargetError || !editingDefect) return;
    const fallback = editingDefect;
    setEditingDefect(null);
    setEditDefectDialogOpen(false);
    queryClient.removeQueries({ queryKey: defectsKeys.editTarget(fallback.id) });
    navigate(`/defects?id=${fallback.id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defectEditTargetError, editingDefect]);
  const [cancellingDefect, setCancellingDefect] = useState<DefectCardData | null>(null);
  const [reopeningDefect, setReopeningDefect] = useState<DefectCardData | null>(null);
  const [deletingDefect, setDeletingDefect] = useState<DefectCardData | null>(null);

  // Inspection dialogs state
  const [editingInspection, setEditingInspection] = useState<InspectionCardData | null>(null);
  const [editInspectionDialogOpen, setEditInspectionDialogOpen] = useState(false);
  const [cancellingInspection, setCancellingInspection] = useState<{
    id: string;
    checklistName: string;
  } | null>(null);
  const [cancelInspectionDialogOpen, setCancelInspectionDialogOpen] = useState(false);
  const [deletingInspection, setDeletingInspection] = useState<{
    id: string;
    checklistName: string;
  } | null>(null);
  const [deleteInspectionDialogOpen, setDeleteInspectionDialogOpen] = useState(false);
  const [bulkReassignDialogOpen, setBulkReassignDialogOpen] = useState(false);

  // Handle URL parameter to highlight/scroll to specific task
  useEffect(() => {
    const taskId = searchParams.get("id");
    if (taskId && tasks.length > 0) {
      const task = tasks.find((t) => t.id === taskId);
      if (task) {
        if (task.type === "defect") {
          navigate(`/defects?id=${taskId}`);
        } else {
          navigate(`/inspections/${taskId}`);
        }
        searchParams.delete("id");
        setSearchParams(searchParams, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, tasks, navigate]);

  const filteredTasks = filterTasks(tasks, activeTab, searchQuery);

  const stats = computeTaskStats(tasks);

  // TES-2237 (US4, Goal-Gradient): "X von Y erledigt (diese Woche)" — makes completed work
  // visible instead of only ever showing remaining/overdue counts (see KPI grid below).
  const weekProgress = useMemo(() => computeWeekProgress(tasks), [tasks]);
  const weekProgressBarValue = computeProgressBarWidth(weekProgress.done, weekProgress.total);
  // Copilot review (CHK PR #860): the shared Progress wrapper never forwards `value` to Radix's
  // Root, so it exposes neither an accessible value nor role by itself. `aria-valuenow` here is
  // the HONEST raw ratio (never the visual never-0% floor above) — screen readers must hear the
  // real percentage, not the "always slightly visible" rendering affordance.
  const weekProgressAriaValueNow =
    weekProgress.total > 0 ? Math.round((weekProgress.done / weekProgress.total) * 100) : 0;
  const weekProgressLabel = t("tasks.weekProgress.label", {
    done: weekProgress.done,
    total: weekProgress.total,
  });

  // TES-2076 (D-UI): Namen der vertretenen Prinzipale (User/Gruppe) für die "Als Stellvertreter"-Karten.
  const { userNames: deputyUserNames, groupNames: deputyGroupNames } = useDeputyPrincipals(tasks);

  // TES-2076: öffnet das vertretene Ursprungs-Item in seiner Detail-Ansicht (Mangel bzw. Prüfung).
  // Der Verifizieren-Pfad zu-verifizierender Prüfungen lebt — wie bei inspection_verification — in der
  // Prüfungs-Detailansicht; verteilen/claimen (je-Typ-Issues) hängen sich später hier ein.
  const handleOpenDeputyItem = (entityType: string, entityId: string) => {
    if (entityType === "defect") {
      navigate(`/defects?id=${entityId}`);
    } else {
      navigate(`/inspections/${entityId}`);
    }
  };

  // TES-2083 (Typ 11): der Deputy übernimmt einen claimbaren ownerlosen Gruppen-Mangel des Vertretenen
  // über den BESTEHENDEN Wirkungsspektrum-Claim (defect_self_assign, Mig 334 → assigned_to = auth.uid()).
  // Kein neuer RPC; die Deputy-Beziehung erweitert nur den Claim-Scope (Mig 394). Nur Mängel sind claimbar.
  const handleClaimDeputyItem = async (defectId: string) => {
    const { error } = await supabase.rpc("defect_self_assign", { _defect_id: defectId });
    if (error) {
      logger.error("deputy defect_self_assign failed", error, { defectId });
      toast.error(t("tasks.deputy.claimError"));
      return;
    }
    toast.success(t("tasks.deputy.claimSuccess"));
    invalidateTasks();
  };

  // Verification handler
  const handleVerifyDefect = (defect: DefectCardData) => {
    setVerifyingDefect({
      id: defect.id,
      title: defect.title,
      assigned_to: defect.assigned_to,
      created_by: defect.created_by,
    });
  };

  // Inspection handlers
  const handleStartInspection = async (inspectionId: string) => {
    const { error } = await supabase
      .from("inspections")
      .update({ status: "in_progress", started_at: new Date().toISOString() })
      .eq("id", inspectionId);

    if (error) {
      toast.error(t("tasks.startInspectionError"));
    } else {
      navigate(`/inspections/${inspectionId}`);
    }
  };

  const handleContinueInspection = (inspectionId: string) => {
    navigate(`/inspections/${inspectionId}`);
  };

  const handleViewInspection = (inspectionId: string) => {
    navigate(`/inspections/${inspectionId}`);
  };

  const handleReopenInspection = async (inspectionId: string) => {
    try {
      const { error } = await supabase.rpc("reopen_inspection", {
        _inspection_id: inspectionId,
      });
      if (error) throw error;

      toast.success(t("inspections.reopenSuccess"), {
        description: t("inspections.reopenSuccessDescription"),
      });

      invalidateTasks();
    } catch (error) {
      logger.error("Error reopening inspection", error, { inspectionId });
      toast.error(t("common.error"), {
        description: t("inspections.reopenError"),
      });
    }
  };

  // Defect handlers
  const handleStartProgress = async (defect: DefectCardData) => {
    const { error } = await supabase
      .from("defects")
      .update({ status: "in_progress", updated_at: new Date().toISOString() })
      .eq("id", defect.id);

    if (error) {
      toast.error(t("tasks.updateError"));
    } else {
      // Fire-and-forget: notify without blocking UI
      const notifyUserId = defect.assigned_to ?? defect.created_by ?? user?.id;
      if (notifyUserId) {
        notifyDefectStatusChanged(notifyUserId, defect.title, defect.id, "in_progress").catch(
          (err) =>
            logger.error("Failed to send status change notification", err, {
              defectId: defect.id,
              status: "in_progress",
            }),
        );
        window.dispatchEvent(new Event("notification:optimistic-increment"));
      }

      toast.success(t("tasks.defectStarted"));
      invalidateTasks();
    }
  };

  const handleResolveDefect = (defect: DefectCardData) => {
    setResolvingDefect({ id: defect.id, title: defect.title });
  };

  const handleViewDefect = (defect: DefectCardData) => {
    navigate(`/defects?id=${defect.id}`);
  };

  const handleEditDefect = (defect: DefectCardData) => {
    setEditingDefect(defect);
    setEditDefectDialogOpen(true);
  };

  // TES-2042: Karten-Klick landet direkt im Edit-Modus (gleiche große Detail-Shell wie
  // /defects); Read-only-/externe Rollen und Terminal-Stati (closed/cancelled — dort lebt
  // der Reopen-Flow, das Menü versteckt Edit ebenso) behalten die Detail-Ansicht
  // (Deeplink → View-Dialog). Gate-Parität mit Defects.openDefect.
  const handleOpenDefect = (defect: DefectCardData) => {
    if (isReadOnlyDefectUser || isExternalDefectUser || isTerminalDefectStatus(defect.status)) {
      handleViewDefect(defect);
    } else {
      handleEditDefect(defect);
    }
  };

  const handleCancelDefect = (defect: DefectCardData) => {
    setCancellingDefect(defect);
  };

  const handleReopenDefect = (defect: DefectCardData) => {
    setReopeningDefect(defect);
  };

  const handleDeleteDefect = (defect: DefectCardData) => {
    setDeletingDefect(defect);
  };

  const handleCloseEditDefectDialog = () => {
    setEditingDefect(null);
    setEditDefectDialogOpen(false);
  };

  // Inspection edit/cancel/delete handlers
  const handleEditInspection = (inspection: InspectionCardData) => {
    setEditingInspection(inspection);
    setEditInspectionDialogOpen(true);
  };

  const handleCancelInspection = (inspectionId: string) => {
    const task = tasks.find((t) => t.id === inspectionId && t.inspectionData);
    if (task?.inspectionData) {
      setCancellingInspection({
        id: inspectionId,
        checklistName: task.inspectionData.checklists?.name || "",
      });
      setCancelInspectionDialogOpen(true);
    }
  };

  const handleDeleteInspection = (inspectionId: string, checklistName: string) => {
    setDeletingInspection({ id: inspectionId, checklistName });
    setDeleteInspectionDialogOpen(true);
  };

  // Assignment handlers
  const handleAssignDefect = async (defectId: string, userId: string | null) => {
    try {
      const { error } = await supabase
        .from("defects")
        .update({ assigned_to: userId })
        .eq("id", defectId);

      if (error) throw error;

      toast.success(userId ? t("defects.assignSuccess") : t("defects.unassignSuccess"));
      invalidateTasks();
    } catch (error) {
      logger.error("Error assigning defect", error, { defectId, assigneeId: userId });
      toast.error(t("defects.assignError"));
    }
  };

  const handleAssignInspection = async (inspectionId: string, userId: string) => {
    try {
      const { error } = await supabase
        .from("inspections")
        .update({ assigned_to: userId })
        .eq("id", inspectionId);

      if (error) throw error;

      toast.success(t("inspections.assignSuccess"));
      invalidateTasks();
    } catch (error) {
      logger.error("Error assigning inspection", error, { inspectionId, assigneeId: userId });
      toast.error(t("inspections.assignError"));
    }
  };

  // Get tasks with unavailable assignees for bulk reassign
  const unavailableAssigneeTasks = getUnavailableAssigneeTasks(tasks);

  // Bulk reassign handler
  const handleBulkReassign = async (newUserId: string) => {
    const defectIds = unavailableAssigneeTasks
      .filter(
        (t) =>
          (t.type === "defect" || t.type === "verification" || t.type === "unavailable_defect") &&
          t.defectData,
      )
      .map((t) => t.id);
    const inspectionIds = unavailableAssigneeTasks
      .filter(
        (t) => (t.type === "inspection" || t.type === "unavailable_inspection") && t.inspectionData,
      )
      .map((t) => t.id);

    try {
      // Atomic bulk reassignment via DB function (single transaction)
      const { error } = await supabase.rpc("bulk_reassign_tasks", {
        _defect_ids: defectIds,
        _inspection_ids: inspectionIds,
        _new_assignee: newUserId,
      });
      if (error) throw error;

      // Server-side bulk notification (single edge function call)
      const allEntities = [
        ...unavailableAssigneeTasks
          .filter(
            (t) =>
              (t.type === "defect" ||
                t.type === "verification" ||
                t.type === "unavailable_defect") &&
              t.defectData,
          )
          .map((t) => ({
            entityType: "defect" as const,
            entityId: t.id,
            entityTitle: t.defectData?.title || "",
            creatorId: t.defectData?.created_by,
          })),
        ...unavailableAssigneeTasks
          .filter(
            (t) =>
              (t.type === "inspection" || t.type === "unavailable_inspection") && t.inspectionData,
          )
          .map((t) => ({
            entityType: "inspection" as const,
            entityId: t.id,
            entityTitle: t.inspectionData?.checklists?.name || "",
            creatorId: t.inspectionData?.created_by,
          })),
      ];

      notifyBulkReassignment(allEntities, newUserId);

      toast.success(t("bulkReassign.success", { count: defectIds.length + inspectionIds.length }));
      invalidateTasks();
    } catch (error) {
      logger.error("Error bulk reassigning tasks", error, {
        defectCount: defectIds.length,
        inspectionCount: inspectionIds.length,
        assigneeId: newUserId,
      });
      toast.error(t("bulkReassign.error"));
    }
  };

  // L6-4 (TES-1870): die "ohne Bearbeiter"-Karten (zugewiesen / unavailable / ownerlose Gruppen-Queue)
  // rendern identisch bis auf den React-Key — als Helfer extrahiert (entzerrt die Render-Closure + DRY).
  // Callers gate on task.defectData / task.inspectionData before invoking, so the data is present.
  const renderUnassignedDefectCard = (task: UnifiedTask, keyPrefix: string) => (
    <DefectCard
      key={`${keyPrefix}-${task.id}`}
      defect={task.defectData!}
      showMenu={true}
      currentUserId={user?.id}
      isQualitaetsmanager={isQualitaetsmanager}
      readOnly={isReadOnlyDefectUser}
      onView={handleViewDefect}
      onOpen={handleOpenDefect}
      onEdit={handleEditDefect}
      onCancel={handleCancelDefect}
      onReopen={handleReopenDefect}
      onDelete={handleDeleteDefect}
      onAssign={handleAssignDefect}
      assignableUsers={assignableUsers}
    />
  );

  const renderUnassignedInspectionCard = (task: UnifiedTask, keyPrefix: string) => (
    <InspectionCard
      key={`${keyPrefix}-${task.id}`}
      inspection={task.inspectionData!}
      currentUserId={user?.id}
      isQualitaetsmanager={isQualitaetsmanager}
      onView={handleViewInspection}
      onEdit={handleEditInspection}
      onCancel={handleCancelInspection}
      onDelete={canDeleteInspection(task.inspectionData!) ? handleDeleteInspection : undefined}
      onAssign={handleAssignInspection}
      assignableUsers={inspectionAssignableUsers}
      showMenu={true}
    />
  );

  // TES-2058 ([R12]): qm_admin-Oversight-Karten — bewusst FEST readOnly/ohne Schreib-Handler (nicht an
  // isReadOnlyDefectUser gekoppelt, die ist für customer_approver gedacht). qm_admin hat seit
  // DATAFIX_037/Mig 399 kein defects:manage/create und nie inspections:execute/edit — ein aktionsfähiger
  // Button (z. B. DefectCard's AssignAction, die nur auf Status/Zuweisung prüft, NICHT auf Recht) würde
  // RLS-seitig ins Leere laufen (0 Zeilen, false-positive Erfolgs-Toast). "Details anzeigen" bleibt über
  // das readOnly-Aktionsmenü (DefectCard) bzw. den Karten-Klick (InspectionCard onView) erreichbar.
  const renderOversightDefectCard = (task: UnifiedTask) => (
    <DefectCard
      key={`oversight-defect-${task.id}`}
      defect={task.defectData!}
      showMenu={true}
      currentUserId={user?.id}
      isQualitaetsmanager={isQualitaetsmanager}
      readOnly
      onView={handleViewDefect}
    />
  );

  const renderOversightInspectionCard = (task: UnifiedTask) => (
    <InspectionCard
      key={`oversight-inspection-${task.id}`}
      inspection={task.inspectionData!}
      currentUserId={user?.id}
      isQualitaetsmanager={isQualitaetsmanager}
      onView={handleViewInspection}
      showMenu={false}
    />
  );

  return (
    <PageContainer className="space-y-6" data-testid="tasks-page-loaded">
      {/* Header - always visible */}
      <PageHeader
        icon={ListTodo}
        title={t("tasks.myTasks")}
        titleSuffix={!isLoading && <OverdueBadge count={stats.overdue} />}
        subtitle={t("tasks.assignedSubtitle")}
      />

      {/* Show skeleton when loading initial data */}
      {isLoading && tasks.length === 0 ? (
        <TasksSkeleton />
      ) : (
        <>
          {/* TES-2237 (US4, Goal-Gradient): Wochen-Fortschritt — nur sichtbar, wenn diese Woche
              überhaupt etwas ansteht (fällig oder bereits erledigt), sonst wäre die Karte leer/
              bedeutungslos. Der Balken macht Erledigtes sichtbar (nie 0% Breite, wenn done > 0) —
              die angezeigte Zahl bleibt immer ehrlich (computeProgressBarWidth floort nur die
              gerenderte Breite, nicht den Text). */}
          {weekProgress.total > 0 && (
            <div
              className="rounded-lg border border-outline-variant bg-surface-container-lowest p-4"
              data-testid="tasks-week-progress"
            >
              <span className="text-sm font-medium text-foreground">{weekProgressLabel}</span>
              {/* Copilot review (CHK PR #860): accessible semantics live on THIS wrapper (honest
                  aria-valuenow, not the visual never-0% floor); the shared <Progress> below is
                  purely decorative (aria-hidden) since it never forwards `value` to Radix's own
                  aria-valuenow/role wiring. */}
              <div
                role="progressbar"
                aria-valuenow={weekProgressAriaValueNow}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={weekProgressLabel}
                className="mt-2"
              >
                <Progress
                  value={weekProgressBarValue}
                  variant="meter"
                  data-testid="tasks-week-progress-bar"
                  aria-hidden="true"
                />
              </div>
            </div>
          )}

          {/* Stats - clickable to filter (DS KpiStatCard form, per-tab Ring-Schema erhalten) */}
          <div className="grid gap-4 container-stats-6">
            <KpiStatCard
              tone="primary"
              icon={<ListTodo />}
              label={t("common.total")}
              value={<span className="tabular-nums">{stats.total}</span>}
              onClick={() => setActiveTab("all")}
              className={cn(
                "cursor-pointer transition-colors hover:bg-surface-container-low",
                activeTab === "all" && "ring-2 ring-primary",
              )}
            />
            <KpiStatCard
              tone="secondary"
              icon={<AlertCircle />}
              label={t("dashboard.openAll")}
              value={<span className="tabular-nums text-warning">{stats.openAll}</span>}
              onClick={() => setActiveTab("openAll")}
              className={cn(
                "cursor-pointer transition-colors hover:bg-surface-container-low",
                activeTab === "openAll" && "ring-2 ring-warning",
              )}
            />
            <KpiStatCard
              tone="secondary"
              icon={<Clock />}
              label={t("dashboard.notStarted")}
              value={<span className="tabular-nums text-warning">{stats.notStarted}</span>}
              onClick={() => setActiveTab("notStarted")}
              className={cn(
                "cursor-pointer transition-colors hover:bg-surface-container-low",
                activeTab === "notStarted" && "ring-2 ring-warning",
              )}
            />
            <KpiStatCard
              tone="primary"
              icon={<ClipboardList />}
              label={t("dashboard.inProgress")}
              value={<span className="tabular-nums text-info">{stats.in_progress}</span>}
              onClick={() => setActiveTab("in_progress")}
              className={cn(
                "cursor-pointer transition-colors hover:bg-surface-container-low",
                activeTab === "in_progress" && "ring-2 ring-info",
              )}
            />
            <KpiStatCard
              tone="primary"
              icon={<CheckCircle2 />}
              label={t("dashboard.toVerify")}
              value={<span className="tabular-nums text-info">{stats.verification}</span>}
              onClick={() => setActiveTab("verification")}
              className={cn(
                "cursor-pointer transition-colors hover:bg-surface-container-low",
                activeTab === "verification" && "ring-2 ring-info",
              )}
            />
            {/* TES-2053: eigene Sektion "Zu verifizieren (Prüfungen)" — nur sichtbar, wenn der
                Caller zu-verifizierende Prüfungen hat (analog groupUnassigned/unavailableAssignee). */}
            {stats.inspectionVerification > 0 && (
              <KpiStatCard
                tone="primary"
                icon={<CheckCircle2 />}
                label={t("tasks.inspectionVerificationTab")}
                value={
                  <span className="tabular-nums text-info">{stats.inspectionVerification}</span>
                }
                onClick={() => setActiveTab("inspectionVerification")}
                className={cn(
                  "cursor-pointer transition-colors hover:bg-surface-container-low",
                  activeTab === "inspectionVerification" && "ring-2 ring-info",
                )}
              />
            )}
            {/* TES-2076 (D-UI · LH §L6.3): eigene Sektion "Aufgaben als Stellvertreter" — nur sichtbar,
                wenn der Caller Vertretungs-Aufgaben hat (analog inspectionVerification/groupUnassigned). */}
            {stats.deputy > 0 && (
              <KpiStatCard
                tone="primary"
                icon={<UserCheck />}
                label={t("tasks.deputy.tab")}
                value={<span className="tabular-nums text-primary">{stats.deputy}</span>}
                onClick={() => setActiveTab("deputy")}
                className={cn(
                  "cursor-pointer transition-colors hover:bg-surface-container-low",
                  activeTab === "deputy" && "ring-2 ring-primary",
                )}
              />
            )}
            <KpiStatCard
              tone="error"
              icon={<Clock />}
              label={t("dashboard.overdue")}
              value={<span className="tabular-nums text-destructive">{stats.overdue}</span>}
              onClick={() => setActiveTab("overdue")}
              className={cn(
                "cursor-pointer transition-colors hover:bg-surface-container-low",
                activeTab === "overdue" && "ring-2 ring-destructive",
              )}
            />
            <KpiStatCard
              tone="primary"
              icon={<CheckCircle2 />}
              label={t("dashboard.completed")}
              value={<span className="tabular-nums text-success">{stats.completed}</span>}
              onClick={() => setActiveTab("completed")}
              className={cn(
                "cursor-pointer transition-colors hover:bg-surface-container-low",
                activeTab === "completed" && "ring-2 ring-success",
              )}
            />
            <KpiStatCard
              tone="primary"
              icon={<UserPlus />}
              label={t("dashboard.unassigned")}
              value={<span className="tabular-nums text-info">{stats.unassigned}</span>}
              onClick={() => setActiveTab("unassigned")}
              className={cn(
                "cursor-pointer transition-colors hover:bg-surface-container-low",
                activeTab === "unassigned" && "ring-2 ring-info",
              )}
            />
            {stats.groupUnassigned > 0 && (
              <KpiStatCard
                tone="primary"
                icon={<Users />}
                label={t("myGroups.title")}
                value={<span className="tabular-nums text-primary">{stats.groupUnassigned}</span>}
                onClick={() => setActiveTab("groupUnassigned")}
                className={cn(
                  "cursor-pointer transition-colors hover:bg-surface-container-low",
                  activeTab === "groupUnassigned" && "ring-2 ring-primary",
                )}
              />
            )}
            {stats.unavailableAssignee > 0 && (
              <KpiStatCard
                tone="secondary"
                icon={<UserCog />}
                label={t("defects.tabs.unavailableAssignee")}
                value={
                  <span className="tabular-nums text-warning">{stats.unavailableAssignee}</span>
                }
                onClick={() => setActiveTab("unavailableAssignee")}
                className={cn(
                  "cursor-pointer transition-colors hover:bg-surface-container-low",
                  activeTab === "unavailableAssignee" && "ring-2 ring-warning",
                )}
              />
            )}
          </div>

          {/* Search & Team Filter */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder={t("tasks.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <TeamFilterDropdown value={teamFilter} onChange={setTeamFilter} />
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <ScrollableTabsList activeTab={activeTab}>
              <TabsTrigger
                value="all"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                {t("common.total")} ({stats.total})
              </TabsTrigger>
              <TabsTrigger
                value="openAll"
                data-testid="tasks-tab-open-all"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                {t("dashboard.openAll")} ({stats.openAll})
              </TabsTrigger>
              <TabsTrigger
                value="notStarted"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                {t("dashboard.notStarted")} ({stats.notStarted})
              </TabsTrigger>
              <TabsTrigger
                value="in_progress"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                {t("dashboard.inProgress")} ({stats.in_progress})
              </TabsTrigger>
              {/* TES-2217 (S3): die QM-Verifikations-Queue wohnt in „Meine Aufgaben" (dieser Tab),
                  NICHT in „Meine Gruppen" — data-testid ankert die Heimat-Assertion. */}
              <TabsTrigger
                value="verification"
                data-testid="tasks-tab-verification"
                className="gap-1 data-[state=active]:bg-info data-[state=active]:text-info-foreground"
              >
                <CheckCircle2 className="h-3 w-3" />
                {t("dashboard.toVerify")} ({stats.verification})
              </TabsTrigger>
              {/* TES-2053: Tab "Zu verifizieren (Prüfungen)" — analog der Mängel-Verifikation,
                  aber nur bei vorhandenen zu-verifizierenden Prüfungen (Verifizierer/Deputy). */}
              {stats.inspectionVerification > 0 && (
                <TabsTrigger
                  value="inspectionVerification"
                  data-testid="tasks-tab-inspection-verification"
                  className="gap-1 data-[state=active]:bg-info data-[state=active]:text-info-foreground"
                >
                  <CheckCircle2 className="h-3 w-3" />
                  {t("tasks.inspectionVerificationTab")} ({stats.inspectionVerification})
                </TabsTrigger>
              )}
              {/* TES-2076: Tab "Aufgaben als Stellvertreter" — nur bei vorhandenen Vertretungs-Aufgaben. */}
              {stats.deputy > 0 && (
                <TabsTrigger
                  value="deputy"
                  data-testid="tasks-tab-deputy"
                  className="gap-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  <UserCheck className="h-3 w-3" />
                  {t("tasks.deputy.tab")} ({stats.deputy})
                </TabsTrigger>
              )}
              <TabsTrigger
                value="overdue"
                className="gap-1 text-destructive data-[state=active]:bg-destructive data-[state=active]:text-destructive-foreground"
              >
                <AlertCircle className="h-3 w-3" />
                {t("dashboard.overdue")} ({stats.overdue})
              </TabsTrigger>
              <TabsTrigger
                value="completed"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                {t("dashboard.completed")} ({stats.completed})
              </TabsTrigger>
              <TabsTrigger
                value="unassigned"
                className="gap-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <UserPlus className="h-3 w-3" />
                {t("dashboard.unassigned")} ({stats.unassigned})
              </TabsTrigger>
              {stats.groupUnassigned > 0 && (
                <TabsTrigger
                  value="groupUnassigned"
                  data-testid="my-groups-view"
                  className="gap-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  <Users className="h-3 w-3" />
                  {t("myGroups.title")} ({stats.groupUnassigned})
                </TabsTrigger>
              )}
              {stats.pointsActions > 0 && (
                <TabsTrigger
                  value="pointsActions"
                  className="gap-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  <ClipboardList className="h-3 w-3" />
                  {t("tasks.pointsActionsTab")} ({stats.pointsActions})
                </TabsTrigger>
              )}
              <TabsTrigger
                value="defects"
                className="gap-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <AlertTriangle className="h-3 w-3" />
                {t("defects.title")} ({stats.defects})
              </TabsTrigger>
              <TabsTrigger
                value="inspections"
                className="gap-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                <ClipboardList className="h-3 w-3" />
                {t("inspections.title")} ({stats.inspections})
              </TabsTrigger>
              <TabsTrigger
                value="unavailableAssignee"
                className="gap-1 text-warning data-[state=active]:bg-warning data-[state=active]:text-warning-foreground"
              >
                <AlertCircle className="h-3 w-3" />
                {t("defects.tabs.unavailableAssignee")} ({stats.unavailableAssignee})
              </TabsTrigger>
              {activeTab !== "all" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActiveTab("all")}
                  className="ml-2 h-8 gap-1.5 bg-gradient-to-r from-primary/20 to-info/20 border-primary/30 text-primary hover:from-primary/30 hover:to-info/30 hover:text-primary"
                >
                  <X className="h-3.5 w-3.5" />
                  {t("common.clearAllFilters")}
                </Button>
              )}
            </ScrollableTabsList>

            {/* Bulk Reassign Button for unavailableAssignee tab */}
            {activeTab === "unavailableAssignee" && stats.unavailableAssignee > 0 && (
              <div className="flex justify-end mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBulkReassignDialogOpen(true)}
                  className="gap-2 text-warning border-warning/30 hover:bg-warning/10"
                >
                  <Users className="h-4 w-4" />
                  {t("tasks.bulkReassign.buttonLabel")} ({stats.unavailableAssignee})
                </Button>
              </div>
            )}

            <TabsContent value={activeTab} className="mt-4">
              {filteredTasks.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <CheckCircle2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium">{t("tasks.noTasks")}</h3>
                    <p className="text-muted-foreground">
                      {activeTab === "all"
                        ? t("tasks.noAssignedTasks")
                        : activeTab === "overdue"
                          ? t("tasks.noOverdueTasks")
                          : activeTab === "unassigned"
                            ? t("tasks.noUnassignedTasks")
                            : t("tasks.noTasksInCategory")}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div
                  className={cn(
                    "space-y-4",
                    isFetching && tasks.length > 0 && "opacity-60 transition-opacity duration-200",
                  )}
                >
                  {/* fallow-ignore-next-line complexity */}
                  {filteredTasks.map((task, index) => {
                    if (task.type === "defect" && task.defectData) {
                      return (
                        <DefectCard
                          key={`defect-${task.id}`}
                          defect={task.defectData}
                          showMenu={true}
                          currentUserId={user?.id}
                          isQualitaetsmanager={isQualitaetsmanager}
                          readOnly={isReadOnlyDefectUser}
                          onView={handleViewDefect}
                          onOpen={handleOpenDefect}
                          onEdit={handleEditDefect}
                          onStartProgress={handleStartProgress}
                          onResolve={handleResolveDefect}
                          onVerify={handleVerifyDefect}
                          onCancel={handleCancelDefect}
                          onReopen={handleReopenDefect}
                          onDelete={handleDeleteDefect}
                          onAssign={handleAssignDefect}
                          assignableUsers={assignableUsers}
                        />
                      );
                    }

                    if (task.type === "verification" && task.defectData) {
                      return (
                        <DefectCard
                          key={`verification-${task.id}`}
                          defect={task.defectData}
                          showMenu={true}
                          currentUserId={user?.id}
                          isQualitaetsmanager={isQualitaetsmanager}
                          readOnly={isReadOnlyDefectUser}
                          onView={handleViewDefect}
                          onOpen={handleOpenDefect}
                          onEdit={handleEditDefect}
                          onVerify={handleVerifyDefect}
                          onCancel={handleCancelDefect}
                          onReopen={handleReopenDefect}
                          onDelete={handleDeleteDefect}
                          onAssign={handleAssignDefect}
                          assignableUsers={assignableUsers}
                        />
                      );
                    }

                    if (task.type === "inspection" && task.inspectionData) {
                      return (
                        <InspectionCard
                          key={`inspection-${task.id}`}
                          inspection={task.inspectionData}
                          currentUserId={user?.id}
                          isQualitaetsmanager={isQualitaetsmanager}
                          onStart={handleStartInspection}
                          onContinue={handleContinueInspection}
                          onView={handleViewInspection}
                          onEdit={handleEditInspection}
                          onReopen={handleReopenInspection}
                          onCancel={handleCancelInspection}
                          onDelete={
                            canDeleteInspection(task.inspectionData)
                              ? handleDeleteInspection
                              : undefined
                          }
                          onAssign={handleAssignInspection}
                          assignableUsers={inspectionAssignableUsers}
                          showMenu={true}
                        />
                      );
                    }

                    if (task.type === "unassigned_defect" && task.defectData) {
                      return renderUnassignedDefectCard(task, "unassigned-defect");
                    }

                    if (task.type === "unassigned_inspection" && task.inspectionData) {
                      return renderUnassignedInspectionCard(task, "unassigned-inspection");
                    }

                    if (task.type === "unavailable_defect" && task.defectData) {
                      return renderUnassignedDefectCard(task, "unavailable-defect");
                    }

                    if (task.type === "unavailable_inspection" && task.inspectionData) {
                      return renderUnassignedInspectionCard(task, "unavailable-inspection");
                    }

                    // TES-1863 / L6-4 "Meine Gruppen": ownerlose Gruppen-Mängel — read + claim (assign to self).
                    if (task.type === "group_unassigned_defect" && task.defectData) {
                      return renderUnassignedDefectCard(task, "group-unassigned-defect");
                    }

                    // TES-1870 "Meine Gruppen": ownerlose Gruppen-Prüfungen — read + claim (self-assign).
                    if (task.type === "group_unassigned_inspection" && task.inspectionData) {
                      return renderUnassignedInspectionCard(task, "group-unassigned-inspection");
                    }

                    // TES-2053 (LH §L6.3): zu-verifizierende Prüfung (Caller = Verifizierer/Deputy) —
                    // Klick/„Anzeigen" öffnet die Prüfung; die Verifikation lebt in der Detail-Ansicht.
                    if (task.type === "inspection_verification" && task.inspectionData) {
                      return (
                        <div
                          key={`inspection-verification-${task.id}`}
                          data-testid={`task-inspection-verification-${task.id}`}
                        >
                          <InspectionCard
                            inspection={task.inspectionData}
                            currentUserId={user?.id}
                            isQualitaetsmanager={isQualitaetsmanager}
                            onView={handleViewInspection}
                            showMenu={false}
                          />
                        </div>
                      );
                    }

                    // TES-1869 (L6-3): zugewiesener Prüfpunkt — im Kontext der Eltern-Prüfung bearbeitet.
                    if (task.type === "inspection_point" && task.inspectionPointData) {
                      return (
                        <InspectionPointCard
                          key={`inspection-point-${task.id}`}
                          point={task.inspectionPointData}
                          onOpenInspection={handleViewInspection}
                        />
                      );
                    }

                    // TES-1869 (L6-3): zugewiesene PT-TSK-Maßnahme — gehört zu einem Mangel.
                    if (task.type === "pt_action" && task.ptActionData) {
                      return (
                        <PtActionCard
                          key={`pt-action-${task.id}`}
                          action={task.ptActionData}
                          onOpenDefect={(defectId) => navigate(`/defects?id=${defectId}`)}
                        />
                      );
                    }

                    // TES-2076 (D-UI · LH §L6.3): "Aufgaben als Stellvertreter" — zeigt vertretenen
                    // Prinzipal (User/Gruppe) + Ebene, öffnet das Objekt bzw. den Verifizieren-Pfad.
                    if (task.type === "deputy" && task.deputyData) {
                      const d = task.deputyData;
                      const principalName = d.principal_user_id
                        ? deputyUserNames.get(d.principal_user_id)
                        : d.principal_group_id
                          ? deputyGroupNames.get(d.principal_group_id)
                          : null;
                      return (
                        <DeputyTaskCard
                          key={`deputy-${task.id}`}
                          task={d}
                          principalName={principalName}
                          onOpen={handleOpenDeputyItem}
                          onClaim={handleClaimDeputyItem}
                        />
                      );
                    }

                    // TES-2058 ([R12]): qm_admin-Oversight — read-only Karte, keine Schreib-Handler.
                    if (task.type === "oversight_defect" && task.defectData) {
                      return renderOversightDefectCard(task);
                    }
                    if (task.type === "oversight_inspection" && task.inspectionData) {
                      return renderOversightInspectionCard(task);
                    }

                    return null;
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Resolve Defect Dialog */}
          <ResolveDefectDialog
            open={!!resolvingDefect}
            onOpenChange={(open) => !open && setResolvingDefect(null)}
            defect={resolvingDefect}
            onResolved={invalidateTasks}
          />

          {/* Verify Defect Dialog */}
          <VerifyDefectDialog
            open={!!verifyingDefect}
            onOpenChange={(open) => !open && setVerifyingDefect(null)}
            defect={verifyingDefect}
            onVerified={invalidateTasks}
          />

          {/* Edit Defect Dialog — konsolidierter DefectBaseDialog (TES-1962): DefectCardData ist
              schlanker als DefectWithDetails → das Edit-Target fetcht das volle Defect + die
              Fachabteilung-Gruppe by id (kein Typ-Drift); Dialog rendert erst mit geladenem
              Target (assignedGroup wird nur beim Mount übernommen). Entry ist über die Karte
              bereits permission-gated → canEdit spiegelt das bisherige Verhalten (editierbar,
              sobald geöffnet). */}
          {editingDefect && defectEditTarget && (
            <DefectBaseDialog
              open={editDefectDialogOpen}
              onOpenChange={handleCloseEditDefectDialog}
              defect={defectEditTarget.defect}
              assignedGroup={defectEditTarget.assignedGroup}
              canEdit
              initialMode="edit"
              onChanged={invalidateTasks}
            />
          )}

          {/* Cancel Defect Dialog */}
          <CancelDefectDialog
            open={!!cancellingDefect}
            onOpenChange={(open) => !open && setCancellingDefect(null)}
            defect={
              cancellingDefect
                ? {
                    id: cancellingDefect.id,
                    title: cancellingDefect.title,
                    assigned_to: cancellingDefect.assigned_to,
                  }
                : null
            }
            onCancelled={invalidateTasks}
          />

          {/* Reopen Defect Dialog */}
          <ReopenDefectDialog
            open={!!reopeningDefect}
            onOpenChange={(open) => !open && setReopeningDefect(null)}
            onConfirm={async () => {
              if (!reopeningDefect) return;
              try {
                const { error } = await supabase
                  .from("defects")
                  .update({
                    status: "open",
                    resolved_at: null,
                    resolution_notes: null,
                    resolution_time_minutes: null,
                    cancellation_reason: null,
                    cancelled_at: null,
                    updated_at: new Date().toISOString(),
                  })
                  .eq("id", reopeningDefect.id);

                if (error) throw error;

                // Fire-and-forget: notify without blocking UI
                const notifyUserId =
                  reopeningDefect.assigned_to ?? reopeningDefect.created_by ?? user?.id;
                if (notifyUserId) {
                  notifyDefectStatusChanged(
                    notifyUserId,
                    reopeningDefect.title,
                    reopeningDefect.id,
                    "open",
                  ).catch((err) =>
                    logger.error("Failed to send reopen notification", err, {
                      defectId: reopeningDefect.id,
                      status: "open",
                    }),
                  );
                  window.dispatchEvent(new Event("notification:optimistic-increment"));
                }

                toast.success(t("defects.reopenSuccess"));
                setReopeningDefect(null);
                invalidateTasks();
              } catch (error) {
                logger.error("Error reopening defect", error, { defectId: reopeningDefect.id });
                toast.error(t("defects.reopenError"));
              }
            }}
          />

          {/* Delete Defect Dialog */}
          <DeleteDefectDialog
            open={!!deletingDefect}
            onOpenChange={(open) => !open && setDeletingDefect(null)}
            defectTitle={deletingDefect?.title || ""}
            onConfirm={async () => {
              if (!deletingDefect) return;
              try {
                const { error } = await supabase
                  .from("defects")
                  .update({ deleted_at: new Date().toISOString() })
                  .eq("id", deletingDefect.id);

                if (error) throw error;

                toast.success(t("defects.deleteSuccess"));
                setDeletingDefect(null);
                invalidateTasks();
              } catch (error) {
                logger.error("Error deleting defect", error, { defectId: deletingDefect.id });
                toast.error(t("defects.deleteError"));
              }
            }}
          />

          {/* Edit Inspection Dialog */}
          <EditInspectionDialog
            open={editInspectionDialogOpen}
            onOpenChange={setEditInspectionDialogOpen}
            inspection={
              editingInspection
                ? {
                    id: editingInspection.id,
                    status: editingInspection.status,
                    due_date: editingInspection.due_date,
                    assigned_to: editingInspection.assigned_to,
                    created_by: editingInspection.created_by ?? null,
                    position_id: editingInspection.position_id ?? null,
                    notes: editingInspection.notes ?? null,
                    team_id: editingInspection.team_id ?? null,
                    checklists: {
                      name: editingInspection.checklists.name,
                      version: editingInspection.checklists.version ?? null,
                    },
                  }
                : null
            }
            onUpdated={invalidateTasks}
          />

          {/* Cancel Inspection Dialog */}
          <CancelInspectionDialog
            open={cancelInspectionDialogOpen}
            onOpenChange={setCancelInspectionDialogOpen}
            inspection={cancellingInspection}
            onCancelled={invalidateTasks}
          />

          {/* Delete Inspection Dialog */}
          <DeleteInspectionDialog
            open={deleteInspectionDialogOpen}
            onOpenChange={setDeleteInspectionDialogOpen}
            inspection={deletingInspection}
            onDeleted={invalidateTasks}
          />

          {/* Bulk Reassign Dialog */}
          <BulkReassignDialog
            open={bulkReassignDialogOpen}
            onOpenChange={setBulkReassignDialogOpen}
            itemCount={stats.unavailableAssignee}
            entityType="tasks"
            assignableUsers={assignableUsers}
            onConfirm={handleBulkReassign}
          />
        </>
      )}
    </PageContainer>
  );
}
